import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Send, X, Paperclip } from 'lucide-react'

import { getMe, getMessage, sendMessage } from '../lib/api'
import AddressField from '../components/AddressField'

function splitAddresses(raw: string): string[] {
  return raw.split(/[;,\n\s]+/).map(s => s.trim()).filter(Boolean)
}

function extractError(err: unknown): string {
  if (!err) return 'unknown error'
  const msg = err instanceof Error ? err.message : String(err)
  const m = msg.match(/API \d+: (.+)/)
  const clean = m ? m[1] : msg
  // Detect the SES sandbox error and give a helpful hint.
  if (/sandbox|not verified|not authorized/i.test(clean)) {
    return clean + '\n\nHint: SES is still in sandbox mode. Only verified recipient addresses can receive mail. Add the address to the SES sandbox-verified list or file a production-access request in the SES console.'
  }
  return clean
}

export default function Compose() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const mode = params.get('mode')                     // reply | reply-all | forward | null
  const inReplyTo = params.get('in_reply_to') || undefined
  const originalId = params.get('original_id')
  const originalMailbox = params.get('mailbox') || undefined

  const [to, setTo] = useState<string[]>(splitAddresses(params.get('to') || ''))
  const [cc, setCc] = useState<string[]>(splitAddresses(params.get('cc') || ''))
  const [bcc, setBcc] = useState<string[]>([])
  const [subject, setSubject] = useState(params.get('subject') || '')
  const [body, setBody] = useState('')
  const [showCc, setShowCc] = useState(cc.length > 0)
  const [showBcc, setShowBcc] = useState(false)

  const me = useQuery({ queryKey: ['me'], queryFn: getMe })
  const fromOptions = me.data?.from_addresses || (me.data?.email ? [me.data.email] : [])
  const initialFrom = params.get('from') || me.data?.email || fromOptions[0] || ''
  const [from, setFrom] = useState(initialFrom)
  useEffect(() => {
    if (!from && (me.data?.email || fromOptions[0])) {
      setFrom(me.data?.email || fromOptions[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.data?.email, fromOptions.length])

  useEffect(() => {
    if (!mode || !originalId || !originalMailbox) return
    getMessage(originalId, originalMailbox).then(m => {
      const prefix = mode === 'forward' ? 'Fwd: ' : 'Re: '
      if (!subject && m.subject) {
        setSubject(prefix + m.subject.replace(/^(Re|Fwd):\s*/i, ''))
      }
      if (mode === 'reply' && !to.length) setTo([m.sender])
      if (mode === 'reply-all' && !to.length) {
        setTo([m.sender])
        const others = splitAddresses(m.recipient || '').filter(x => x !== m.sender)
        if (others.length) {
          setCc(others)
          setShowCc(true)
        }
      }
      const quoted = (m.body_text || '').split('\n').slice(0, 200).map(l => '> ' + l).join('\n')
      setBody(`\n\nOn ${m.received_at}, ${m.sender} wrote:\n${quoted}\n`)
    }).catch(() => { /* ignore */ })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, originalId, originalMailbox])

  const send = useMutation({
    mutationFn: () => sendMessage({
      to, cc: cc.length ? cc : undefined, bcc: bcc.length ? bcc : undefined,
      subject, body_text: body, in_reply_to: inReplyTo,
      from: from || undefined,
    }),
    onSuccess: () => navigate(-1),
  })

  return (
    <div className="h-full flex flex-col">
      <header className="border-b border-line px-6 py-4 flex items-center justify-between">
        <div>
          <div className="h-eyebrow">Compose</div>
          <h1 className="text-xl font-semibold text-ink-900 tracking-tight">
            {mode === 'reply' ? 'Reply' :
             mode === 'reply-all' ? 'Reply all' :
             mode === 'forward' ? 'Forward' : 'New message'}
          </h1>
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate(-1)} className="pill-ghost">
            <X size={16} /> Cancel
          </button>
          <button
            onClick={() => send.mutate()}
            disabled={send.isPending || !to.length || !subject}
            className="pill-primary disabled:opacity-50"
          >
            <Send size={16} />
            {send.isPending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </header>

      <div className="p-6 space-y-2 flex-1 flex flex-col">
        {fromOptions.length > 1 && (
          <div className="flex items-baseline gap-3 py-1 border-b border-line">
            <label className="h-eyebrow w-14">From</label>
            <select
              value={from}
              onChange={e => setFrom(e.target.value)}
              className="flex-1 bg-transparent border-none outline-none py-1 text-sm text-ink-900 cursor-pointer hover:text-ink-700 focus:text-ink-700 pr-6"
            >
              {fromOptions.map(addr => <option key={addr} value={addr}>{addr}</option>)}
            </select>
          </div>
        )}
        <div className="flex items-baseline gap-3">
          <div className="flex-1">
            <AddressField label="To" addresses={to} onChange={setTo} autoFocus />
          </div>
          <div className="flex gap-2 pt-2">
            {!showCc && <button onClick={() => setShowCc(true)}
                                className="text-xs text-ink-500 hover:text-ink-900">Cc</button>}
            {!showBcc && <button onClick={() => setShowBcc(true)}
                                 className="text-xs text-ink-500 hover:text-ink-900">Bcc</button>}
          </div>
        </div>

        {showCc && (
          <AddressField label="Cc" addresses={cc} onChange={setCc}
                        onRemoveField={() => { setShowCc(false); setCc([]) }} />
        )}

        {showBcc && (
          <AddressField label="Bcc" addresses={bcc} onChange={setBcc}
                        onRemoveField={() => { setShowBcc(false); setBcc([]) }} />
        )}

        <div className="flex items-baseline gap-3 py-1 border-b border-line">
          <label className="h-eyebrow w-14">Subject</label>
          <input value={subject} onChange={e => setSubject(e.target.value)}
                 className="flex-1 bg-transparent border-none outline-none py-1 text-sm text-ink-900 placeholder:text-ink-300" />
        </div>

        <textarea
          value={body} onChange={e => setBody(e.target.value)}
          placeholder="Write your message…"
          className="flex-1 min-h-[300px] resize-none card-inset p-4 text-sm text-ink-900 focus:outline-none focus:shadow-focus mt-2"
        />

        <div className="flex items-center gap-3 text-xs text-ink-500">
          <button className="pill-ghost text-xs" title="Attachments — coming soon">
            <Paperclip size={12} /> Attach (soon)
          </button>
          <span>Drag chips between fields. Type & press Enter/Tab/Space to lock in.</span>
        </div>

        {send.isError && (
          <div className="text-sm text-red-700 bg-red-50 p-3 rounded-soft border border-red-100 whitespace-pre-wrap">
            <div className="font-medium mb-0.5">Send failed</div>
            <div className="font-mono text-xs">{extractError(send.error)}</div>
          </div>
        )}
      </div>
    </div>
  )
}
